import { useState, useEffect } from 'react';
import api from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Coins, Plus, Minus, Settings, ArrowUpDown, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CreditManagementPage() {
  const [tenants, setTenants] = useState([]);
  const [pricing, setPricing] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [txnTotal, setTxnTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAdjust, setShowAdjust] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [adjustForm, setAdjustForm] = useState({ tenant_id: '', amount: '', description: '', type: 'add' });
  const [pricingForm, setPricingForm] = useState({});

  const load = async () => {
    try {
      const [t, p, tx] = await Promise.all([
        api.get('/admin/tenants-usage'),
        api.get('/admin/credits/pricing'),
        api.get('/admin/credits/transactions?limit=50'),
      ]);
      setTenants(t.data);
      setPricing(p.data);
      setPricingForm(JSON.parse(JSON.stringify(p.data)));
      setTransactions(tx.data.items);
      setTxnTotal(tx.data.total);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleAdjust = async () => {
    const amt = parseFloat(adjustForm.amount);
    if (!amt || !adjustForm.tenant_id) { toast.error('Gerekli alanları doldurun'); return; }
    try {
      const endpoint = adjustForm.type === 'add' ? '/admin/credits/add' : '/admin/credits/subtract';
      await api.post(endpoint, {
        tenant_id: adjustForm.tenant_id,
        amount: Math.abs(amt),
        description: adjustForm.description || (adjustForm.type === 'add' ? 'Manuel kredi ekleme' : 'Manuel kredi düşme'),
      });
      toast.success(adjustForm.type === 'add' ? 'Kredi eklendi' : 'Kredi düşüldü');
      setShowAdjust(false);
      setAdjustForm({ tenant_id: '', amount: '', description: '', type: 'add' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handlePricingSave = async () => {
    try {
      await api.put('/admin/credits/pricing', { rules: pricingForm });
      toast.success('Fiyatlandırma güncellendi');
      setShowPricing(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  const txnTypeLabels = {
    credit_added: { label: 'Eklendi', style: 'bg-green-500/10 text-green-400 border-green-500/20' },
    credit_reserved: { label: 'Rezerve', style: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
    credit_consumed: { label: 'Harcandı', style: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' },
    credit_released: { label: 'Serbest', style: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
    credit_refunded: { label: 'İade', style: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
    manual_adjustment: { label: 'Manuel', style: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  };

  if (loading) return <div className="text-[#A3A3A3] font-ibm">Kredi yönetimi yükleniyor...</div>;

  return (
    <div data-testid="credit-management-page" className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between pb-6 border-b border-[#262626]">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-white tracking-tight">Kredi Yönetimi</h1>
          <p className="text-[#A3A3A3] font-ibm text-sm mt-1">Bayi kredi bakiyeleri ve işlem geçmişi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowPricing(true)} data-testid="pricing-btn" className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">
            <Settings size={16} className="mr-2" /> Fiyatlandırma
          </Button>
          <Button onClick={() => setShowAdjust(true)} data-testid="adjust-credits-btn" className="bg-white text-black hover:bg-[#E5E5E5] active:scale-[0.98] transition-transform duration-100">
            <Coins size={16} className="mr-2" /> Kredi Ekle / Düş
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="bg-[#141414] border-[#262626]">
            <CardHeader><CardTitle className="font-outfit text-lg text-white">Bayi Kredi Bakiyeleri</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-[#262626] hover:bg-transparent">
                    <TableHead className="text-[#A3A3A3] font-ibm">Bayi</TableHead>
                    <TableHead className="text-[#A3A3A3] font-ibm text-right">Bakiye</TableHead>
                    <TableHead className="text-[#A3A3A3] font-ibm text-right">Rezerve</TableHead>
                    <TableHead className="text-[#A3A3A3] font-ibm text-right">Kullanılan</TableHead>
                    <TableHead className="text-[#A3A3A3] font-ibm text-right">Kullanılabilir</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tenants.map((t) => {
                    const avail = (t.credit_balance || 0) - (t.credit_reserved || 0);
                    return (
                      <TableRow key={t.tenant_id} className="border-[#262626] hover:bg-white/5" data-testid={`credit-row-${t.tenant_id}`}>
                        <TableCell className="font-ibm text-white">
                          <div className="flex items-center gap-2">
                            <Building2 size={14} className="text-[#FACC15]" /> {t.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-jetbrains text-[#FACC15]">{t.credit_balance ?? 0}</TableCell>
                        <TableCell className="text-right font-jetbrains text-blue-400">{t.credit_reserved ?? 0}</TableCell>
                        <TableCell className="text-right font-jetbrains text-[#A3A3A3]">{t.credit_used ?? 0}</TableCell>
                        <TableCell className="text-right font-jetbrains text-green-400">{avail}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-[#141414] border-[#262626]">
          <CardHeader><CardTitle className="font-outfit text-lg text-white">Fiyatlandırma</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(pricing).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-md border border-[#1A1A1A]">
                  <span className="text-sm text-[#A3A3A3] font-ibm">{val.label}</span>
                  <Badge variant="outline" className="bg-[#FACC15]/10 text-[#FACC15] border-[#FACC15]/20 font-jetbrains">{val.credits} kredi</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#141414] border-[#262626]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-outfit text-lg text-white">Kredi İşlem Geçmişi</CardTitle>
            <span className="text-xs text-[#737373] font-ibm">{txnTotal} toplam işlem</span>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="border-[#262626] hover:bg-transparent">
                  <TableHead className="text-[#A3A3A3] font-ibm">Tarih</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Tür</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm text-right">Miktar</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">Açıklama</TableHead>
                  <TableHead className="text-[#A3A3A3] font-ibm">İş ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => {
                  const info = txnTypeLabels[tx.transaction_type] || { label: tx.transaction_type, style: '' };
                  return (
                    <TableRow key={tx.id} className="border-[#262626] hover:bg-white/5">
                      <TableCell className="font-jetbrains text-xs text-[#737373]">{new Date(tx.created_at).toLocaleString('tr-TR')}</TableCell>
                      <TableCell><Badge variant="outline" className={info.style}>{info.label}</Badge></TableCell>
                      <TableCell className={`text-right font-jetbrains ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {tx.amount >= 0 ? '+' : ''}{tx.amount}
                      </TableCell>
                      <TableCell className="text-[#A3A3A3] font-ibm text-sm max-w-[200px] truncate">{tx.description || '-'}</TableCell>
                      <TableCell className="font-jetbrains text-xs text-[#525252]">{tx.related_job_id?.slice(0, 8) || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      <Dialog open={showAdjust} onOpenChange={setShowAdjust}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white">
          <DialogHeader><DialogTitle className="font-outfit text-white">Kredi Ekle / Düş</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Bayi</Label>
              <Select value={adjustForm.tenant_id} onValueChange={(v) => setAdjustForm({ ...adjustForm, tenant_id: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="adjust-tenant-select"><SelectValue placeholder="Bayi seçin" /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {tenants.map((t) => <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">İşlem Türü</Label>
              <Select value={adjustForm.type} onValueChange={(v) => setAdjustForm({ ...adjustForm, type: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-[#262626] text-white mt-1" data-testid="adjust-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  <SelectItem value="add">Kredi Ekle</SelectItem>
                  <SelectItem value="subtract">Kredi Düş</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Miktar</Label>
              <Input type="number" value={adjustForm.amount} onChange={(e) => setAdjustForm({ ...adjustForm, amount: e.target.value })} data-testid="adjust-amount-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="100" />
            </div>
            <div>
              <Label className="text-[#A3A3A3] font-ibm text-sm">Açıklama</Label>
              <Input value={adjustForm.description} onChange={(e) => setAdjustForm({ ...adjustForm, description: e.target.value })} data-testid="adjust-desc-input" className="bg-[#0A0A0A] border-[#262626] text-white mt-1" placeholder="Manuel düzeltme..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdjust(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handleAdjust} data-testid="adjust-submit-btn" className="bg-white text-black hover:bg-[#E5E5E5]">Uygula</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showPricing} onOpenChange={setShowPricing}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit text-white">Fiyatlandırma Kuralları</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {Object.entries(pricingForm).map(([key, val]) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-sm text-[#A3A3A3] font-ibm flex-1">{val.label}</span>
                <Input
                  type="number" value={val.credits}
                  onChange={(e) => setPricingForm({ ...pricingForm, [key]: { ...val, credits: parseFloat(e.target.value) || 0 } })}
                  className="bg-[#0A0A0A] border-[#262626] text-white w-24 text-right"
                  data-testid={`pricing-${key}`}
                />
                <span className="text-xs text-[#737373] font-ibm w-12">kredi</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPricing(false)} className="border-[#262626] text-[#A3A3A3] hover:text-white hover:bg-white/5">İptal</Button>
            <Button onClick={handlePricingSave} data-testid="pricing-save-btn" className="bg-white text-black hover:bg-[#E5E5E5]">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
